/**
 * TBGenSurv Module 3 — Pipeline Data Store
 * 
 * Simulates real WGS pipeline runs on M. tuberculosis isolates.
 * Reference genome: M. tuberculosis H37Rv (GenBank AL123456.3)
 * 
 * Pipeline tools simulated:
 *   QC:         FastQC + MultiQC
 *   Trimming:   Trimmomatic 0.39
 *   Alignment:  BWA-MEM 0.7.17
 *   Processing: SAMtools 1.17 + Picard 3.0
 *   Variants:   GATK 4.4 HaplotypeCaller + snippy 4.6
 *   Annotation: SnpEff + WHO 2021 TB Mutation Catalogue
 *   Lineage:    TB-Profiler + Coll 2014 lineage scheme
 *   ML:         Custom k-mer model (Module 4 bridge)
 */

// ── PIPELINE STAGES ───────────────────────────────────────────────────
export const PIPELINE_STAGES = [
  { id: 'intake',      label: 'Sample Intake',        icon: '📥', tool: 'TBGenSurv intake',   duration: '2 min'  },
  { id: 'qc_raw',      label: 'Raw Read QC',          icon: '🔍', tool: 'FastQC 0.12',        duration: '4 min'  },
  { id: 'trimming',    label: 'Adapter Trimming',     icon: '✂️',  tool: 'Trimmomatic 0.39',  duration: '6 min'  },
  { id: 'qc_trimmed',  label: 'Post-trim QC',         icon: '🔍', tool: 'FastQC 0.12',        duration: '3 min'  },
  { id: 'alignment',   label: 'Reference Alignment',  icon: '🎯', tool: 'BWA-MEM 0.7.17',    duration: '12 min' },
  { id: 'processing',  label: 'BAM Processing',       icon: '⚙️',  tool: 'SAMtools + Picard', duration: '8 min'  },
  { id: 'variants',    label: 'Variant Calling',      icon: '🧬', tool: 'GATK + snippy',     duration: '15 min' },
  { id: 'annotation',  label: 'Resistance Annotation',icon: '📋', tool: 'WHO 2021 Catalogue', duration: '5 min'  },
  { id: 'lineage',     label: 'Lineage Typing',       icon: '🌍', tool: 'TB-Profiler',        duration: '4 min'  },
  { id: 'ml_predict',  label: 'ML Prediction',        icon: '🤖', tool: 'TBGenSurv k-mer ML', duration: '3 min'  },
  { id: 'report',      label: 'Report Generation',    icon: '📊', tool: 'TBGenSurv Report',   duration: '2 min'  },
];

// ── WGS JOBS ──────────────────────────────────────────────────────────
export let WGS_JOBS = [
  {
    id: 'wgs-001',
    sample_code: 'TBS-HAR-20260310-0039',
    sample_id: 'smp-004',
    submitted: '2026-03-16T10:05:00Z',
    started:   '2026-03-16T10:07:00Z',
    completed: '2026-03-16T11:32:00Z',
    status: 'COMPLETE',
    priority: 'urgent',
    submitter: 'lab.senior',
    clinic: 'Epworth Clinic',
    province: 'Harare',
    trigger_reason: 'MDR-TB — Rifampicin + Isoniazid resistance',

    // QC Metrics
    qc: {
      raw_reads:        3842600,
      raw_reads_r2:     3842600,
      raw_quality_avg:  34.2,
      raw_gc_pct:       65.3,
      adapter_pct:      4.2,
      trimmed_reads:    3679812,
      trimmed_pct:      95.8,
      trimmed_quality:  36.8,
      mapping_rate:     99.4,
      mean_depth:       142.3,
      genome_coverage:  99.87,
      reference_genome: 'H37Rv (AL123456.3)',
      genome_size_bp:   4411532,
      duplicate_pct:    1.8,
    },

    // Detected variants
    variants: [
      { gene: 'rpoB',  pos: 761155, ref: 'TCG', alt: 'TTG', aa: 'S450L', type: 'missense',  drug: 'Rifampicin',  confidence: 'High', who_grade: 1, reads_ref: 0,   reads_alt: 142 },
      { gene: 'katG',  pos: 2155168,ref: 'AGC', alt: 'ACC', aa: 'S315T', type: 'missense',  drug: 'Isoniazid',   confidence: 'High', who_grade: 1, reads_ref: 2,   reads_alt: 138 },
      { gene: 'inhA',  pos: 1673425,ref: 'C',   alt: 'T',   aa: 'c-15t', type: 'promoter',  drug: 'Isoniazid',   confidence: 'High', who_grade: 1, reads_ref: 5,   reads_alt: 137 },
      { gene: 'embB',  pos: 4247431,ref: 'ATG', alt: 'GTG', aa: 'M306V', type: 'missense',  drug: 'Ethambutol',  confidence: 'Mod',  who_grade: 2, reads_ref: 68,  reads_alt: 74  },
      { gene: 'gyrA',  pos: 7582,   ref: 'GAC', alt: 'GTC', aa: 'D94G',  type: 'missense',  drug: 'Fluoroquinolones', confidence: 'High', who_grade: 1, reads_ref: 1, reads_alt: 141 },
    ],

    // Lineage
    lineage: {
      main_lineage:  'Lineage 4',
      sub_lineage:   '4.1.2.1',
      family:        'Euro-American',
      notes:         'Beijing-like sub-lineage common in southern Africa',
    },

    // ML prediction
    ml_prediction: {
      model:       'TBGenSurv k-mer XGBoost v1.0',
      features:    8192,
      kmer_size:   6,
      predictions: [
        { drug: 'Rifampicin',    prediction: 'Resistant',   prob: 0.997, actual: 'Resistant'   },
        { drug: 'Isoniazid',     prediction: 'Resistant',   prob: 0.994, actual: 'Resistant'   },
        { drug: 'Pyrazinamide',  prediction: 'Susceptible', prob: 0.881, actual: 'Susceptible' },
        { drug: 'Ethambutol',    prediction: 'Resistant',   prob: 0.743, actual: 'Resistant'   },
        { drug: 'Moxifloxacin',  prediction: 'Resistant',   prob: 0.912, actual: 'Resistant'   },
      ],
      accuracy:    0.974,
      auc_roc:     0.991,
    },

    // Final classification
    classification: 'Pre-XDR-TB',
    wgs_classification_note: 'WGS confirms MDR-TB with additional fluoroquinolone resistance (gyrA D94G) — upgraded to Pre-XDR-TB.',

    // Stage completion
    stages: PIPELINE_STAGES.map(s => ({ ...s, status: 'complete', completed_at: '2026-03-16T11:30:00Z' })),
  },

  {
    id: 'wgs-002',
    sample_code: 'TBS-BUL-20260312-0018',
    sample_id: 'smp-002',
    submitted: '2026-03-20T09:00:00Z',
    started:   '2026-03-20T09:02:00Z',
    completed: null,
    status: 'RUNNING',
    priority: 'urgent',
    submitter: 'lab.tech.nmrl',
    clinic: 'Mpilo Central Hospital',
    province: 'Bulawayo',
    trigger_reason: 'MDR-TB — GeneXpert RIF resistance detected',

    qc: {
      raw_reads: 4102400, raw_reads_r2: 4102400, raw_quality_avg: 33.8,
      raw_gc_pct: 65.1, adapter_pct: 3.9, trimmed_reads: 3938150,
      trimmed_pct: 96.0, trimmed_quality: 36.5, mapping_rate: 98.9,
      mean_depth: 134.7, genome_coverage: 99.71,
      reference_genome: 'H37Rv (AL123456.3)', genome_size_bp: 4411532, duplicate_pct: 2.1,
    },
    variants: [],
    lineage: null,
    ml_prediction: null,
    classification: null,

    stages: PIPELINE_STAGES.map((s, i) => ({
      ...s,
      status: i < 6 ? 'complete' : i === 6 ? 'running' : 'pending',
      completed_at: i < 6 ? '2026-03-20T09:45:00Z' : null,
      progress: i === 6 ? 62 : null,
    })),
  },

  {
    id: 'wgs-003',
    sample_code: 'TBS-MVG-20260318-0004',
    sample_id: 'smp-007',
    submitted: '2026-03-20T14:00:00Z',
    started: null,
    completed: null,
    status: 'QUEUED',
    priority: 'routine',
    submitter: 'lab.tech.nmrl',
    clinic: 'Masvingo Provincial Hospital',
    province: 'Masvingo',
    trigger_reason: 'RR-TB — Rifampicin resistance only',

    qc: null, variants: [], lineage: null, ml_prediction: null, classification: null,
    stages: PIPELINE_STAGES.map(s => ({ ...s, status: 'pending', completed_at: null })),
  },
];

// ── WHO MUTATION CATALOGUE GRADES ────────────────────────────────────
export const WHO_GRADES = {
  1: { label: 'Assoc. w/ resistance',      color: '#E74C3C', bg: '#E74C3C20' },
  2: { label: 'Probable resistance',        color: '#F39C12', bg: '#F39C1220' },
  3: { label: 'Uncertain significance',     color: '#95A5A6', bg: '#95A5A620' },
  4: { label: 'Unlikely resistance',        color: '#27AE60', bg: '#27AE6020' },
  5: { label: 'Not assoc. w/ resistance',   color: '#27AE60', bg: '#27AE6020' },
};

// ── REFERENCE GENOME INFO ─────────────────────────────────────────────
export const REFERENCE = {
  name:     'Mycobacterium tuberculosis H37Rv',
  accession:'AL123456.3',
  length:   4411532,
  gc_pct:   65.6,
  source:   'NCBI RefSeq',
  genes:    4018,
};

// ── LINEAGE SYSTEM ────────────────────────────────────────────────────
export const LINEAGES = {
  'Lineage 1': { name: 'Indo-Oceanic',     color: '#3498DB', common_in: 'South Asia, East Africa' },
  'Lineage 2': { name: 'East Asian',       color: '#E74C3C', common_in: 'East Asia, South Africa (Beijing)' },
  'Lineage 3': { name: 'Delhi/CAS',        color: '#F39C12', common_in: 'Central/South Asia, East Africa' },
  'Lineage 4': { name: 'Euro-American',    color: '#27AE60', common_in: 'Europe, Americas, Africa' },
  'Lineage 5': { name: 'West African 1',   color: '#9B59B6', common_in: 'West Africa' },
  'Lineage 6': { name: 'West African 2',   color: '#1ABC9C', common_in: 'West Africa' },
  'Lineage 7': { name: 'Ethiopian',        color: '#E67E22', common_in: 'Ethiopia' },
  'Lineage 8': { name: 'Ugandan',          color: '#2ECC71', common_in: 'Uganda' },
  'Lineage 9': { name: 'East African',     color: '#16A085', common_in: 'Kenya, Tanzania' },
};

// ── ACTIONS ───────────────────────────────────────────────────────────
export function submitJob(sampleCode, sampleId, triggerReason, submitter, clinic, province) {
  const newJob = {
    id: `wgs-${Date.now()}`,
    sample_code: sampleCode,
    sample_id: sampleId,
    submitted: new Date().toISOString(),
    started: null, completed: null,
    status: 'QUEUED',
    priority: 'routine',
    submitter, clinic, province,
    trigger_reason: triggerReason,
    qc: null, variants: [], lineage: null, ml_prediction: null, classification: null,
    stages: PIPELINE_STAGES.map(s => ({ ...s, status: 'pending', completed_at: null })),
  };
  WGS_JOBS = [newJob, ...WGS_JOBS];
  return newJob;
}

export function getPipelineStats() {
  return {
    total_jobs:    WGS_JOBS.length,
    queued:        WGS_JOBS.filter(j => j.status === 'QUEUED').length,
    running:       WGS_JOBS.filter(j => j.status === 'RUNNING').length,
    complete:      WGS_JOBS.filter(j => j.status === 'COMPLETE').length,
    failed:        WGS_JOBS.filter(j => j.status === 'FAILED').length,
    avg_depth:     137.5,
    avg_coverage:  99.7,
    mdr_confirmed: WGS_JOBS.filter(j => j.classification?.includes('MDR') || j.classification?.includes('XDR')).length,
  };
}
